import { BOT_MODELS, botLabel } from '@chess/core'
import { useEffect, useState, type JSX } from 'react'
import { roomPath } from '../lib/router.js'
import { signInHref, type Session } from '../lib/session.js'

export interface ArenaProps {
  readonly session: Session
  readonly navigate: (to: string) => void
}

const ARENA_MODELS = BOT_MODELS.filter((model) => model.kind !== 'engine')

interface CurrentMatch {
  readonly roomId: string | null
  readonly whiteModel?: string
  readonly blackModel?: string
}

interface LeaderboardRow {
  readonly model: string
  readonly won: number
  readonly lost: number
  readonly drawn: number
  readonly games: number
}

/**
 * Watch the current match, or start one.
 *
 * Only one match runs at a time — the point is a comparison you can actually
 * follow, not a wall of simultaneous games — so this page is mostly a picker
 * plus a leaderboard, not a busy arena floor.
 */
export const Arena = ({ session, navigate }: ArenaProps): JSX.Element => {
  const [current, setCurrent] = useState<CurrentMatch | null>(null)
  const [leaderboard, setLeaderboard] = useState<readonly LeaderboardRow[]>([])
  const [white, setWhite] = useState(ARENA_MODELS[0]?.id ?? '')
  const [black, setBlack] = useState(ARENA_MODELS[1]?.id ?? ARENA_MODELS[0]?.id ?? '')
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch('/api/arena/current')
      .then((response) => response.json())
      .then((body: CurrentMatch) => {
        if (!cancelled) setCurrent(body)
      })
      .catch(() => undefined)
    fetch('/api/arena/leaderboard')
      .then((response) => response.json())
      .then((body: { models: readonly LeaderboardRow[] }) => {
        if (!cancelled) setLeaderboard(body.models)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  const startMatch = async (): Promise<void> => {
    setStarting(true)
    setError(null)
    try {
      const response = await fetch('/api/arena/matches', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ models: [white, black] }),
      })
      const body = (await response.json()) as { roomId?: string; error?: string }
      if (response.status === 409 && body.roomId !== undefined) {
        navigate(roomPath(body.roomId))
        return
      }
      if (!response.ok || body.roomId === undefined) throw new Error(body.error ?? `server said ${response.status}`)
      navigate(roomPath(body.roomId))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'could not start a match')
      setStarting(false)
    }
  }

  return (
    <main className="mx-auto w-full max-w-lg px-4 py-6 safe-top safe-bottom">
      <header className="flex items-center justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-sm font-semibold text-ink-dim hover:text-ink"
          >
            ← Home
          </button>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">Arena</h1>
          <p className="mt-0.5 text-sm text-ink-dim">Pick two models. Watch which one wins.</p>
        </div>
      </header>

      {current?.roomId !== null && current !== null && (
        <section className="mt-6 rounded-panel bg-panel p-4 ring-1 ring-accent/40">
          <p className="text-sm font-semibold">A match is live right now</p>
          <p className="mt-1 text-xs text-ink-dim">
            {botLabel(current.whiteModel ?? '')} vs {botLabel(current.blackModel ?? '')}
          </p>
          <button
            type="button"
            onClick={() => navigate(roomPath(current.roomId as string))}
            className="mt-3 w-full rounded bg-accent px-4 py-2.5 text-sm font-bold text-black hover:bg-accent-strong"
          >
            Watch it
          </button>
        </section>
      )}

      <section className="mt-6 rounded-panel bg-panel p-4">
        <p className="text-sm font-semibold">Start a match</p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <ModelPicker label="White" value={white} onChange={setWhite} />
          <ModelPicker label="Black" value={black} onChange={setBlack} />
        </div>

        {session.user === null ? (
          <a
            href={signInHref()}
            className="mt-3 block w-full rounded bg-accent px-4 py-3 text-center text-base font-bold text-black hover:bg-accent-strong"
          >
            Sign in to start a match
          </a>
        ) : (
          <button
            type="button"
            disabled={starting || current?.roomId != null}
            onClick={() => void startMatch()}
            className="mt-3 w-full rounded bg-accent px-4 py-3 text-base font-bold text-black hover:bg-accent-strong disabled:opacity-50"
          >
            {starting ? 'Starting…' : current?.roomId != null ? 'A match is already live' : 'Start match'}
          </button>
        )}
        {error !== null && <p className="mt-2 text-sm text-danger">Could not start a match — {error}.</p>}
        <p className="mt-2 text-center text-[11px] text-ink-dim">
          Blitz, capped at 80 full moves. Archived and scored like any other game.
        </p>
      </section>

      {leaderboard.length > 0 && (
        <section className="mt-6 rounded-panel bg-panel p-4">
          <p className="text-sm font-semibold">Leaderboard</p>
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-ink-dim">
                <th className="pb-1 font-semibold">Model</th>
                <th className="pb-1 text-right font-semibold">W</th>
                <th className="pb-1 text-right font-semibold">L</th>
                <th className="pb-1 text-right font-semibold">D</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((row) => (
                <tr key={row.model} className="border-t border-panel-line">
                  <td className="py-1.5">{botLabel(row.model)}</td>
                  <td className="py-1.5 text-right text-accent">{row.won}</td>
                  <td className="py-1.5 text-right text-danger">{row.lost}</td>
                  <td className="py-1.5 text-right text-ink-dim">{row.drawn}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </main>
  )
}

const ModelPicker = ({
  label,
  value,
  onChange,
}: {
  readonly label: string
  readonly value: string
  readonly onChange: (id: string) => void
}): JSX.Element => (
  <label className="block text-xs font-semibold tracking-wide text-ink-dim uppercase">
    {label}
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="mt-1.5 w-full rounded bg-panel-raised px-3 py-2.5 text-sm font-normal normal-case tracking-normal text-ink outline-none focus:ring-1 focus:ring-accent"
    >
      {ARENA_MODELS.map((model) => (
        <option key={model.id} value={model.id}>
          {model.label}
        </option>
      ))}
    </select>
  </label>
)
